import DealDeliveryPreferenceBadge from '@/components/deal/delivery-preference/deal-delivery-preference-badge';
import { DealPriorityBadge } from '@/components/deal/priority/deal-priority-badge';
import { DealStatusBadge } from '@/components/deal/status/deal-status-badge';
import { TradeInIcon } from '@/components/deal/trade/deal-trade-ins';
import { Button } from '@/components/ui/button';
import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { PDKDeliveryStageIcon } from '@/components/ui/pdk-icons';
import { HOTKEYS } from '@/lib/hotkeys';
import {
  Banknote,
  Calendar,
  CalendarFold,
  CalendarSync,
  CircleUser,
  Landmark,
  ListEnd,
  Lock,
  UserRoundPen,
  Wrench,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type {
  ComboBoxState,
  DeliveryPreferenceFilters,
  DeliveryStageFilters,
  FilterState,
  FinanceManagerFilters,
  FundingMethodFilters,
  LenderFilters,
  PriorityFilters,
  RapidReconStepFilters,
  SalesRepFilters,
  StatusFilters,
} from '../types';
import CreatedDateFiltersComboBoxPopover from './created-date-filters-combobox-popover';
import DeliveryDateFiltersComboBoxPopover from './delivery-date-filters-combobox-popover';
import DeliveryPreferenceFiltersComboBoxPopover from './delivery-preference-filters-combobox-popover';
import DeliveryStageFiltersComboBoxPopover from './delivery-stage-filters-combobox-popover';
import FinanceManagerFiltersComboBoxPopover from './finance-manager-filters-combobox-popover';
import FundingMethodFiltersComboBoxPopover from './funding-method-filters-combobox-popover';
import LastUpdatedDateFiltersComboBoxPopover from './last-updated-date-filters-combobox-popover';
import LenderFiltersComboBoxPopover from './lender-filters-combobox-popover';
import PickupDateFiltersComboBoxPopover from './pickup-date-filters-combobox-popover';
import PriorityFiltersComboBoxPopover from './priority-filters-combobox-popover';
import RapidReconStepFiltersComboBoxPopover from './rapid-recon-step-filters-combobox-popover';
import SalesRepFiltersComboBoxPopover from './sales-rep-filters-combobox-popover';
import StatusFiltersComboBoxPopover from './status-filters-combobox-popover';

export const COMBO_BOX_OPTIONS: Option<string>[] = [
  {
    label: 'Deal Status',
    value: 'status',
    icon: <DealStatusBadge value="new" hideCopy />,
  },
  {
    label: 'Delivery Stage',
    value: 'delivery_stage',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <PDKDeliveryStageIcon className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Priority',
    value: 'priority',
    icon: <DealPriorityBadge value="high" hideCopy />,
  },
  {
    label: 'Delivery Preference',
    value: 'delivery_preference',
    icon: <DealDeliveryPreferenceBadge value="delivery" hideCopy />,
  },
  {
    label: 'Finance Manager',
    value: 'finance_manager',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <UserRoundPen className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Sales Rep',
    value: 'sales_rep',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <CircleUser className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Funding Method',
    value: 'funding_method',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Banknote className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Lender',
    value: 'lender',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Landmark className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Rapid Recon Step',
    value: 'rapid_recon_step',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Wrench className="size-3.5" />
      </Button>
    ),
  },
];

// UX NOTE: These filters are usually toggle filters
export const COMBO_BOX_OPTIONS_2: Option<string>[] = [
  {
    label: 'Delivery queued',
    value: 'delivery_queued',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <ListEnd className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Price-locked',
    value: 'price_locked',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Lock className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Traded-in',
    value: 'traded_in',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <TradeInIcon className="size-3.5" />
      </Button>
    ),
  },
];

export const COMBO_BOX_OPTIONS_3: Option<string>[] = [
  {
    label: 'Delivery Date',
    value: 'delivery_date',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Calendar className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Pickup Date',
    value: 'pickup_date',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <Calendar className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Last Updated Date',
    value: 'last_updated_date',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <CalendarSync className="size-3.5" />
      </Button>
    ),
  },
  {
    label: 'Created Date',
    value: 'created_date',
    icon: (
      <Button variant="ghost" className="p-1 h-fit">
        <CalendarFold className="size-3.5" />
      </Button>
    ),
  },
];

const AllFiltersComboBoxPopover = ({
  comboBoxState,
  children,
  onChange,
  onOpenChange,
  open,
  stayOpenAfterSelection,
  currentFilterState,
}: {
  comboBoxState: ComboBoxState;
  children: ReactNode;
  onChange: (value: string) => void;
  onOpenChange: (value: boolean) => void;
  open: boolean;
  stayOpenAfterSelection: boolean;
  currentFilterState: FilterState | null;
}) => {
  if (!currentFilterState) {
    return (
      <ComboboxPopover
        placeholder="Filters..."
        options={COMBO_BOX_OPTIONS}
        options2={COMBO_BOX_OPTIONS_2}
        options3={COMBO_BOX_OPTIONS_3}
        onChange={onChange}
        align="start"
        endAdornment={<KeyboardKeyIcon>{HOTKEYS.FILTERS}</KeyboardKeyIcon>}
        emptyMessage={undefined}
        open={open}
        onOpenChange={onOpenChange}
        stayOpenAfterSelection={true}
        popoverContentClassName="w-fit min-w-[200px]"
      >
        {children}
      </ComboboxPopover>
    );
  }

  const { filterMetaState, selectedFilters } = currentFilterState;

  if (comboBoxState == 'status' && filterMetaState.type == 'status') {
    return (
      <StatusFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as StatusFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </StatusFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'delivery_stage' &&
    filterMetaState.type == 'delivery_stage'
  ) {
    return (
      <DeliveryStageFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as DeliveryStageFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </DeliveryStageFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'rapid_recon_step' &&
    filterMetaState.type == 'rapid_recon_step'
  ) {
    return (
      <RapidReconStepFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as RapidReconStepFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </RapidReconStepFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'delivery_preference' &&
    filterMetaState.type == 'delivery_preference'
  ) {
    return (
      <DeliveryPreferenceFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
        filters={selectedFilters as DeliveryPreferenceFilters}
      >
        {children}
      </DeliveryPreferenceFiltersComboBoxPopover>
    );
  }

  if (comboBoxState == 'priority' && filterMetaState.type == 'priority') {
    return (
      <PriorityFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as PriorityFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </PriorityFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'finance_manager' &&
    filterMetaState.type == 'finance_manager'
  ) {
    return (
      <FinanceManagerFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as FinanceManagerFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </FinanceManagerFiltersComboBoxPopover>
    );
  }

  if (comboBoxState == 'sales_rep' && filterMetaState.type == 'sales_rep') {
    return (
      <SalesRepFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as SalesRepFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </SalesRepFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'funding_method' &&
    filterMetaState.type == 'funding_method'
  ) {
    return (
      <FundingMethodFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as FundingMethodFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </FundingMethodFiltersComboBoxPopover>
    );
  }

  if (comboBoxState == 'lender' && filterMetaState.type == 'lender') {
    return (
      <LenderFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        filters={selectedFilters as LenderFilters}
        stayOpenAfterSelection={stayOpenAfterSelection}
        align="start"
      >
        {children}
      </LenderFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'delivery_date' &&
    filterMetaState.type == 'delivery_date'
  ) {
    return (
      <DeliveryDateFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        align="start"
      >
        {children}
      </DeliveryDateFiltersComboBoxPopover>
    );
  }

  if (comboBoxState == 'pickup_date' && filterMetaState.type == 'pickup_date') {
    return (
      <PickupDateFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        align="start"
      >
        {children}
      </PickupDateFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'last_updated_date' &&
    filterMetaState.type == 'last_updated_date'
  ) {
    return (
      <LastUpdatedDateFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        align="start"
      >
        {children}
      </LastUpdatedDateFiltersComboBoxPopover>
    );
  }

  if (
    comboBoxState == 'created_date' &&
    filterMetaState.type == 'created_date'
  ) {
    return (
      <CreatedDateFiltersComboBoxPopover
        comboBoxOnChange={onChange}
        comboBoxOpen={open}
        onOpenChange={onOpenChange}
        align="start"
      >
        {children}
      </CreatedDateFiltersComboBoxPopover>
    );
  }

  return (
    <ComboboxPopover
      placeholder={`Placeholder combobox for: ${comboBoxState}`}
      options={COMBO_BOX_OPTIONS}
      options2={COMBO_BOX_OPTIONS_2}
      options3={COMBO_BOX_OPTIONS_3}
      onChange={onChange}
      align="start"
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={open}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={true}
      popoverContentClassName="w-fit min-w-[200px]"
    >
      {children}
    </ComboboxPopover>
  );
};

export default AllFiltersComboBoxPopover;
